/**
 * Domain enums — frontend mirror of app/api/domain/enums.py.
 *
 * The canonical source of truth lives on the backend at GET /config/enums.
 * These local constants are the defaults used until the runtime fetch lands,
 * and they let TypeScript narrow types at compile time.
 *
 * Whenever a new value is added, add it to both places OR rely on the runtime
 * fetch and use the `EnumRegistry` type. See docs/data_model_review.md F-H3.
 */

export const TIPO_PROYECTO_INTERNO = 'Proyecto interno' as const

export const TIPO_PROYECTO_VALUES = [
    TIPO_PROYECTO_INTERNO,
    'Producto',
    'Servicio',
    'Consultoría',
    'I+D+i',
    'PoC',
    'Otros',
] as const

export type TipoProyecto = typeof TIPO_PROYECTO_VALUES[number]

export const PROBABILIDAD_VALUES = ['baja', 'media', 'alta'] as const
export type Probabilidad = typeof PROBABILIDAD_VALUES[number]

export const OFERTA_ESTADO_VALUES = [
    'borrador',
    'en_preparacion',
    'en_revision',
    'presentada',
    'en_negociacion',
    'ganada',
    'perdida',
    'cancelada',
    // Set only by the SharePoint import; it goes back to 'borrador' once nothing is missing.
    'incompleta',
] as const
export type OfertaEstado = typeof OFERTA_ESTADO_VALUES[number]

// Same order and reason as app/api/domain/enums.py: the modal's seven states
// first, then the older values the page labels still recognise (W19).
export const PROYECTO_ESTADO_VALUES = [
    'pendiente',
    'planificacion',
    'en_curso',
    'pausado',
    'completado',
    'mantenimiento',
    'cancelado',
    'no_iniciado',
    'en_espera',
    'finalizado',
    'cerrado',
    'cancelada',
] as const
export type ProyectoEstado = typeof PROYECTO_ESTADO_VALUES[number]

export const FILE_CATEGORY_VALUES = ['oferta', 'rfp'] as const
export type FileCategory = typeof FILE_CATEGORY_VALUES[number]

export const KNOWLEDGE_VALUE_VALUES = ['alto', 'medio', 'bajo', 'nulo'] as const
export type KnowledgeValue = typeof KNOWLEDGE_VALUE_VALUES[number]

export const TEAM_TYPE_VALUES = ['oferta', 'proyecto'] as const
export type TeamType = typeof TEAM_TYPE_VALUES[number]

export const START_AVAILABILITY_VALUES = ['Inmediato', '1 mes', '3 meses', '6 meses'] as const
export type StartAvailability = typeof START_AVAILABILITY_VALUES[number]
export const START_AVAILABILITY_DEFAULT: StartAvailability = 'Inmediato'

export interface EnumRegistry {
    tipo_proyecto: readonly string[]
    probabilidad: readonly string[]
    oferta_estado: readonly string[]
    proyecto_estado: readonly string[]
    file_category: readonly string[]
    knowledge_value: readonly string[]
    team_type: readonly string[]
    start_availability: readonly string[]
}

/** Check whether a tipo_proyecto value represents an internal project.
 *
 * Mirrors backend `is_tipo_proyecto_interno`. Prefer this over inline string
 * comparisons with the Spanish label (F-H1).
 */
export function isTipoProyectoInterno(value: string | null | undefined): boolean {
    return (value ?? '').trim() === TIPO_PROYECTO_INTERNO
}
